import { Card, Text, Group, Stack, Avatar, Badge, ActionIcon, Modal, Button } from '@mantine/core';
import { useState } from 'react';
import { GroupResponse } from '../../types/group';
import trashIcon from '../../assets/icons/trash.svg';
import { useAuthInfo } from '@propelauth/react';
import { showSuccessNotification, showErrorNotification } from '../../utils/notificationUtils';

interface GroupCardProps {
  group: GroupResponse;
  onSelect: () => void;
  accessToken: string;
  deleteGroup: (groupId: number) => Promise<{ success: boolean, error?: string }>;
  onDeleted: (groupId: number) => void;
}

export const GroupCard = ({
  group,
  onSelect,
  accessToken,
  deleteGroup,
  onDeleted
}: GroupCardProps) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuthInfo();

  // Handle potentially missing properties
  const members = group.members || [];
  const activeMembers = members.filter(m => m.status === 'active').length;
  const pendingMembers = members.filter(m => m.status === 'pending').length;
  const ghostMembers = members.filter(m => m.status === 'ghost').length;
  const name = group.name || 'Unnamed Group';
  const currencyCode = group.currency_code || 'USD';
  const balances = group.balances || [];
  const unreadCount = group.unread_count || 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      signDisplay: 'auto',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate total balance for current user
  const calculateTotalBalanceForUser = () => {
    // Find the current user's member record using userId for reliability
    const currentUserId = user?.userId;
    if (!currentUserId) {
      console.warn("[GroupCard] Could not get current user ID from auth context.");
      return 0;
    }

    const currentMember = members.find(m => m.user_id === currentUserId);

    if (!currentMember) {
      console.warn(`[GroupCard] Current user (${currentUserId}) not found among group members. Group ID: ${group.id}`);
      // Attempt fallback match by email ONLY if userId match failed
      const emailMember = members.find(m => user?.email && m.email === user.email);
      if (!emailMember) {
        console.warn(`[GroupCard] Fallback email match also failed for user ${user?.email} in group ${group.id}`);
        return 0;
      }
      console.log(`[GroupCard] Using fallback email match for user ${user?.email} -> member ID ${emailMember.id}`);
      // Proceed with emailMember if found
      // NOTE: This might still be inaccurate if emails are out of sync, but better than nothing.
      // Ideally, ensure GroupMember.user_id is always populated for active members.
    }

    // Use the reliably found member's ID (either via userId or email fallback)
    const memberIdToUse = currentMember?.id || members.find(m => user?.email && m.email === user.email)?.id;

    if (!memberIdToUse) {
      console.error(`[GroupCard] Failed to determine member ID for current user in group ${group.id}`);
      return 0; // Should not happen if either match worked
    }

    // Total amounts owed TO the current user
    const owedToUser = balances
      .filter(b => b.to_member_id === memberIdToUse)
      .reduce((sum, b) => sum + b.amount, 0);

    // Total amounts the current user OWES to others
    const userOwes = balances
      .filter(b => b.from_member_id === memberIdToUse)
      .reduce((sum, b) => sum + b.amount, 0);

    // Return net balance (positive means others owe the user, negative means user owes others)
    return owedToUser - userOwes;
  };

  const totalBalance = calculateTotalBalanceForUser();
  const isPositiveBalance = totalBalance > 0;
  const isNegativeBalance = totalBalance < 0;

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigating to group detail
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteGroup(group.id);
      if (result.success) {
        showSuccessNotification(`"${name}" has been deleted successfully`);
        setDeleteModalOpen(false);
        // Call the onDeleted callback to update parent component
        onDeleted(group.id);
      } else {
        // Show the specific error message returned
        showErrorNotification(null, result.error || 'Failed to delete group');
      }
    } catch (error) {
      showErrorNotification(error, 'Failed to delete group');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card
        p="md"
        withBorder
        onClick={onSelect}
        className="cursor-pointer hover:shadow-md transition-shadow"
      >
        <Group justify="space-between">
          <Group>
            <Avatar
              src={null}
              color="blue"
              radius="xl"
              size="lg"
            >
              {name.charAt(0).toUpperCase()}
            </Avatar>

            <div>
              <Group>
                <Text fw={500} size="lg">{name}</Text>
                {unreadCount > 0 && (
                  <Badge color="red" size="sm">
                    {unreadCount}
                  </Badge>
                )}
              </Group>

              <Text size="sm" c="dimmed">
                {activeMembers} member{activeMembers !== 1 ? 's' : ''}
                {ghostMembers > 0 && ` • ${ghostMembers} ghost${ghostMembers !== 1 ? 's' : ''}`}
                {pendingMembers > 0 && ` • ${pendingMembers} pending`}
              </Text>
            </div>
          </Group>

          <Group>
            <div style={{ textAlign: 'right' }}>
              <Text fw={500} c={isPositiveBalance ? 'green' : isNegativeBalance ? 'red' : undefined}>
                {formatCurrency(totalBalance)}
              </Text>
              <Text size="xs" c="dimmed">
                {isPositiveBalance ? 'You are owed' : isNegativeBalance ? 'You owe' : 'Settled up'}
              </Text>
            </div>

            <ActionIcon
              variant="subtle"
              color="red"
              onClick={handleDeleteClick}
            >
              <img src={trashIcon} alt="Delete Group" width={16} />
            </ActionIcon>
          </Group>
        </Group>
      </Card>

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Group"
        centered
      >
        <Stack>
          <Text>
            Are you sure you want to delete the group "{name}"? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={isDeleting}
            >
              Delete Group
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}; 